import React, { useEffect, useState } from 'react';
import { CheckIcon, ZapIcon, LockIcon, ArrowRightIcon } from 'lucide-react';
function useCountdown(initialSeconds: number) {
  const [seconds, setSeconds] = useState(initialSeconds);
  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(s => s > 0 ? s - 1 : 0);
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds % 3600 / 60);
  const s = seconds % 60;
  return {
    hours: String(h).padStart(2, '0'),
    minutes: String(m).padStart(2, '0'),
    secs: String(s).padStart(2, '0'),
    total: seconds
  };
}
export function PricingSection() {
  const {
    hours,
    minutes,
    secs,
    total
  } = useCountdown(23 * 3600 + 47 * 60 + 12);
  return <section id="pricing" className="relative py-24 px-6 overflow-hidden" style={{
    background: 'linear-gradient(180deg, #0F0F1A 0%, #1A1A2E 40%, #16213E 100%)'
  }} data-id="element-131">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#4361EE]/5 rounded-full blur-3xl pointer-events-none" data-id="element-132" />

      <div className="relative max-w-3xl mx-auto text-center" data-id="element-133">
        {/* Urgency header */}
        <div className="inline-flex items-center gap-2 bg-[#FFD60A]/10 border border-[#FFD60A]/30 rounded-full px-5 py-2 mb-8" data-id="element-134">
          <ZapIcon className="w-4 h-4 text-[#FFD60A]" data-id="element-135" />
          <span className="text-[#FFD60A] text-sm font-bold uppercase tracking-widest" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-136">
            Limited Time Offer — Price Increases When Timer Hits Zero
          </span>
        </div>

        {/* Countdown timer */}
        <div className="mb-10" data-id="element-137">
          <p className="text-[#A0AEC0] text-sm uppercase tracking-widest mb-4" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-138">
            Offer expires in:
          </p>
          <div className="flex items-center justify-center gap-3" data-id="element-139">
            <TimerBlock value={hours} label="Hours" data-id="element-140" />
            <span className="text-[#FFD60A] text-5xl font-bold glow-text-yellow" style={{
            fontFamily: "'Bebas Neue', sans-serif"
          }} data-id="element-141">
              :
            </span>
            <TimerBlock value={minutes} label="Minutes" data-id="element-142" />
            <span className="text-[#FFD60A] text-5xl font-bold glow-text-yellow" style={{
            fontFamily: "'Bebas Neue', sans-serif"
          }} data-id="element-143">
              :
            </span>
            <TimerBlock value={secs} label="Seconds" data-id="element-144" />
          </div>
        </div>

        {/* Pricing card */}
        <div className="rounded-2xl p-8 sm:p-12 border border-[#4361EE]/30 mb-8" style={{
        background: 'linear-gradient(135deg, #0F3460 0%, #16213E 100%)',
        boxShadow: '0 0 60px rgba(67, 97, 238, 0.2), 0 0 120px rgba(67, 97, 238, 0.05)'
      }} data-id="element-145">
          {/* Price display */}
          <div className="mb-8" data-id="element-146">
            <div className="flex items-center justify-center gap-4 mb-2" data-id="element-147">
              <span className="text-[#A0AEC0] text-4xl line-through" style={{
              fontFamily: "'Bebas Neue', sans-serif"
            }} data-id="element-148">
                $97
              </span>
              <span className="text-[#F72585] text-[80px] sm:text-[100px] leading-none glow-text-pink" style={{
              fontFamily: "'Bebas Neue', sans-serif"
            }} data-id="element-149">
                $27
              </span>
            </div>
            <p className="text-[#A0AEC0] text-sm uppercase tracking-widest" style={{
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-150">
              One Time Payment · Instant Access · No Subscription
            </p>
          </div>

          {/* What's included */}
          <div className="text-left mb-8 space-y-3" data-id="element-151">
            <IncludeItem text="ADHD Reset — Complete 7-Module System ($97 value)" data-id="element-152" />
            <IncludeItem text="The ADHD Daily Planner Template ($47 value)" data-id="element-153" />
            <IncludeItem text="Crisis Mode Cheat Sheet ($27 value)" data-id="element-154" />
            <IncludeItem text="The ADHD Partner Guide ($37 value)" data-id="element-155" />
            <IncludeItem text="Lifetime access + all future updates" data-id="element-156" />
            <IncludeItem text="30-day money-back guarantee" data-id="element-157" />
          </div>

          {/* Total value */}
          <div className="rounded-xl px-6 py-4 mb-8 text-center" style={{
          background: 'rgba(255, 214, 10, 0.08)',
          border: '1px solid rgba(255, 214, 10, 0.2)'
        }} data-id="element-158">
            <p className="text-[#A0AEC0] text-sm mb-1" style={{
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-159">
              Total value:
            </p>
            <p className="text-[#FFD60A] text-3xl glow-text-yellow" style={{
            fontFamily: "'Bebas Neue', sans-serif"
          }} data-id="element-160">
              $208 — YOURS FOR JUST $27
            </p>
          </div>

          {/* CTA Button */}
          <button className="group w-full bg-[#F72585] hover:bg-[#E01070] text-white font-bold text-2xl py-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 animate-pulse-glow mb-4" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-161">
            YES — I WANT ADHD RESET FOR $27
            <ArrowRightIcon className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-162" />
          </button>

          {/* Security note */}
          <div className="flex items-center justify-center gap-2 text-[#A0AEC0] text-sm" data-id="element-163">
            <LockIcon className="w-4 h-4" data-id="element-164" />
            <span style={{
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-165">
              Secure checkout · Instant delivery · 256-bit SSL
            </span>
          </div>
        </div>

        {/* Urgency reminder */}
        {total < 3600 && <div className="rounded-xl px-6 py-4 border border-[#F72585]/40 animate-pulse-glow" style={{
        background: 'rgba(247, 37, 133, 0.08)'
      }} data-id="element-166">
            <p className="text-[#F72585] font-bold text-sm" style={{
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-167">
              ⚠️ Less than 1 hour remaining at this price!
            </p>
          </div>}
      </div>
    </section>;
}
function TimerBlock({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="flex flex-col items-center" data-id="element-168">
      <div className="w-20 sm:w-24 h-20 sm:h-24 rounded-xl flex items-center justify-center glow-yellow" style={{
      background: 'linear-gradient(135deg, #1A1A2E, #0F0F1A)',
      border: '2px solid rgba(255, 214, 10, 0.4)'
    }} data-id="element-169">
        <span className="text-[#FFD60A] text-4xl sm:text-5xl leading-none glow-text-yellow" style={{
        fontFamily: "'Bebas Neue', sans-serif"
      }} data-id="element-170">
          {value}
        </span>
      </div>
      <span className="text-[#A0AEC0] text-xs uppercase tracking-widest mt-2" style={{
      fontFamily: "'Inter', sans-serif"
    }} data-id="element-171">
        {label}
      </span>
    </div>;
}
function IncludeItem({
  text
}: {
  text: string;
}) {
  return <div className="flex items-start gap-3" data-id="element-172">
      <div className="w-5 h-5 rounded-full bg-[#4361EE]/20 border border-[#4361EE]/40 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-173">
        <CheckIcon className="w-3 h-3 text-[#4361EE]" data-id="element-174" />
      </div>
      <p className="text-white/90 text-sm" style={{
      fontFamily: "'Inter', sans-serif"
    }} data-id="element-175">
        {text}
      </p>
    </div>;
}